import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { useAuthStore } from '..';
import useChangePasswordValidationSchema from '../hooks/useChangePasswordValidationSchema';
import useClosable from '../hooks/useClosable';
import usePasswordUpdate from '../hooks/usePasswordUpdate';
import { ClosableOrder } from '../store/ClosableStore';
import commonStyles from './Common.module.css';
import editStyles from './Edit.module.css';
import EditControls from './EditControls';
import PasswordInput from './inputs/PasswordInput';
import Modal from './Modal';

function ChangePassword({ onClose }: IChangePasswordProps) {
    const updatePassword = usePasswordUpdate();
    const authStore = useAuthStore();
    const validationSchema = useChangePasswordValidationSchema();
    useClosable(ClosableOrder.High, onClose);

    const formik = useFormik<IChangePasswordState>({
        initialValues: ChangePasswordState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            await updatePassword(values.currentPassword!, values.newPassword!);
            onClose();
        },
    });

    return (
        <Modal header="Change Password" onClose={onClose}>
            <div className={editStyles.column}>
                <PasswordInput formik={formik} label="CURRENT PASSWORD" name="currentPassword" />
                <PasswordInput formik={formik} label="NEW PASSWORD" name="newPassword" />
                <div className={commonStyles.controls}>
                    <div className={commonStyles.controlGroup}>
                        <EditControls
                            isStateChanged={formik.dirty}
                            isUpdateLoading={authStore.isLoading}
                            onUpdate={formik.handleSubmit}
                            onCancel={onClose}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default observer(ChangePassword);

interface IChangePasswordProps {
    onClose: () => void;
}

interface IChangePasswordState {
    currentPassword: string | null;
    newPassword: string | null;
}

class ChangePasswordState implements IChangePasswordState {
    currentPassword: string | null = null;
    newPassword: string | null = null

    static empty() {
        return new ChangePasswordState();
    }
}